import Settings from '@front/lib/definitions'
import localFont from 'next/font/local'
import {Archivo_Narrow} from "next/font/google";
import {Briefcase, MapPin, MoveLeft, MoveRight} from "lucide-react";
import {postMeta} from "./postMeta";

const BasierSquare = localFont({
    src: [
        {
            path: './font/basier/regular/basiersquare-regular-webfont.woff2',
            weight: '400',
            style: 'normal',
        },
        {
            path: './font/basier/regularitalic/basiersquare-regularitalic-webfont.woff2',
            weight: '400',
            style: 'italic',
        },
        {
            path: './font/basier/medium/basiersquare-medium-webfont.woff2',
            weight: '500',
            style: 'normal',
        },
        {
            path: './font/basier/mediumitalic/basiersquare-mediumitalic-webfont.woff2',
            weight: '500',
            style: 'italic',
        },
        {
            path: './font/basier/semibold/basiersquare-semibold-webfont.woff2',
            weight: '600',
            style: 'normal',
        },
        {
            path: './font/basier/semibolditalic/basiersquare-semibolditalic-webfont.woff2',
            weight: '600',
            style: 'italic',
        },
        {
            path: './font/basier/bold/basiersquare-bold-webfont.woff2',
            weight: '700',
            style: 'normal',
        },
        {
            path: './font/basier/bolditalic/basiersquare-bolditalic-webfont.woff2',
            weight: '700',
            style: 'italic',
        },
    ],
    variable: '--font-basier-square', // Optional: for use with CSS variables
});

const BasierSquareMono = localFont({
    src: [
        {
            path: './font/basier-mono/semibold/basiersquaremono-semibold-webfont.woff2',
            weight: '600',
            style: 'normal',
        },
    ],
    variable: '--font-basier-square-mono',
})

const ArchivoNarrow = Archivo_Narrow({
    weight: "700",
    style: "normal",
    subsets: ['latin', 'latin-ext'],
    variable: '--font-archivo-narrow',
})

export const Settings: Settings = {
    frontendUrl: 'https://emerge.dev.kareltropp.cz',
    cdnUrl: 'https://cdn.emerge.dev.kareltropp.cz',
    languages: [
        'cs-CZ',
        'en-GB'
    ],
    defaultLanguage: 'cs-CZ',
    adminLanguage: 'cs',
    appName: 'Emerge',
    cmsName: 'Administrace',
    siteEmail: 'info@emerge.cz',
    fonts: [
        BasierSquare,
        BasierSquareMono,
        ArchivoNarrow
    ],
    bodyClass: 'font-sans',
    customPostTypes: [
        {
            slug: 'job-position',
            label: 'Pracovní pozice',
            icon: Briefcase,
            rewrite: [
                {
                    lang: 'cs',
                    rewrite: 'kariera'
                },
                {
                    lang: 'en',
                    rewrite: 'career'
                }
            ]
        }
    ],
    postMeta: postMeta,
    menuLocations: [
        {
            slug: 'header',
            title: 'Header',
        },
        {
            slug: 'footer-1',
            title: 'Footer 1',
        },
        {
            slug: 'footer-2',
            title: 'Footer 2',
        }
    ],
    globalFields: [
        {
            slug: 'employees',
            title: 'Počet zaměstnanců'
        },
        {
            slug: 'places',
            title: 'Počet provozoven'
        },
        {
            slug: 'storage',
            title: 'Skladová kapacita v m2'
        },
        {
            slug: 'countries',
            title: 'Počet zemí světa'
        },
        {
            slug: 'ozp_percent',
            title: '% zaměstnanců OZP'
        },
        {
            slug: 'ozp_count',
            title: 'Počet OZP'
        },
        {
            slug: 'customer_count',
            title: 'Počet zákazníků'
        },
        {
            slug: 'phone',
            title: 'Telefon'
        },
        {
            slug: 'email',
            title: 'Email'
        },
        {
            slug: 'linkedin_name',
            title: 'Název Linkedin'
        },
        {
            slug: 'linkedin_url',
            title: 'Url Linkedin'
        },
        {
            slug: 'facebook_name',
            title: 'Název Facebook'
        },
        {
            slug: 'facebook_url',
            title: 'Url Facebook'
        }
    ],
    blockConfig: {
        heading: {
            className: 'text-heading-2 mb-2',
            tagName: 'h2',
            isTagEditable: true,
            tags: [
                {
                    tagName: 'h1',
                    className: 'text-heading-1 font-bold',
                },
                {
                    tagName: 'h2',
                    className: 'text-heading-2 font-semibold',
                },
                {
                    tagName: 'h3',
                    className: 'text-heading-3 font-semibold',
                },
            ]
        },
        container: {
            className: 'max-w-(--container-width) px-6 lg:px-4 mx-auto'
        },
        button: {
            className: 'inline-block bg-primary-button text-body-inverse hover:bg-primary-hover border-2 border-primary-button hover:border-primary-hover rounded-full text-small px-4 py-2.5 transition-colors font-medium',
            variants: [
                {
                    name: 'primary',
                    className: 'inline-block bg-primary-button text-body-inverse hover:bg-primary-hover border-2 border-primary-button hover:border-primary-hover rounded-full text-small px-4 py-2.5 transition-colors font-medium'
                },
                {
                    name: 'secondary',
                    className: 'inline-block bg-body-inverse text-heading hover:bg-pomegranate-100 border-2 border-primary-button rounded-full text-small px-4 py-2.5 transition-colors font-medium'
                },
                {
                    name: 'tertiary',
                    className: 'inline-block bg-transparent text-heading hover:border-primary-hover border-2 border-icon-muted rounded-full text-small px-4 py-2.5 transition-colors font-medium'
                },
                {
                    name: 'link',
                    className: 'inline-block text-heading text-small px-4 py-2.5 transition-colors font-medium'
                }
            ],
            icons: [
                MoveRight,
                MoveLeft,
                MapPin
            ],
            iconSize: 16,
            spaceSize: 2,
            iconClassName: ""
        },
        list: {
            className: 'list',
            tags: [
                {
                    tagName: 'ul',
                    className: 'list'
                },
                {
                    tagName: 'ol',
                    className: ''
                }
            ]
        },
        "form-field": {
            className: 'px-3 py-4 lg:px-3 lg:py-3 border border-light focus:outline-0 data-[invalid]:border-primary placeholder:text-heading',
        },
        "form-submit": {
            className: 'inline-block bg-primary-button text-body-inverse hover:bg-primary-hover border-2 border-primary-button hover:border-primary-hover rounded-full text-small px-4 py-2.5 transition-colors font-medium cursor-pointer',
            variants: [
                {
                    name: 'primary',
                    className: 'inline-block bg-primary-button text-body-inverse hover:bg-primary-hover border-2 border-primary-button hover:border-primary-hover rounded-full text-small px-4 py-2.5 transition-colors font-medium cursor-pointer'
                },
            ],
            icons: [
                MoveRight
            ],
            iconSize: 16,
            spaceSize: 2,
            iconClassName: ""
        },
    }
}
